// Active call dock + controls.
//
// W31 (2026-10-20): the dock takes its caller facts from props. The
// CALL_INFO fixture ("Sarah Mitchell", "(615) 555-0148", "03:42") is the
// default ONLY for the preview path; a real session passes the HubSpot
// profile (name + phone) with no invented call state or duration, and
// the Kixie controls stay visible but inert behind the live-write gate.
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import { CALL_INFO } from '../mockData.js';
import { WriteButton } from '../../_w2a_lower/liveWrite.js';

export interface CallDockInfo {
  providerLabel: string;
  providerEvidence: string;
  state: string;
  /** Empty string hides the clock (no call in progress). */
  duration: string;
  callerName: string;
  callerNumber: string;
  initials: string;
  /** Eyebrow above the caller name, e.g. "CONNECTED CALL" or "CUSTOMER PHONE". */
  directionLabel: string;
}

export const PREVIEW_CALL_INFO: CallDockInfo = {
  ...CALL_INFO,
  directionLabel: 'CONNECTED CALL',
};

export interface CallDockProps {
  onEndCall: () => void;
  onTransfer: () => void;
  onMute: () => void;
  muted: boolean;
  info?: CallDockInfo;
  /** True on a real session: End / Transfer / Mute are inert. */
  controlsPaused?: boolean;
}

export function CallDock({
  onEndCall,
  onTransfer,
  onMute,
  muted,
  info = PREVIEW_CALL_INFO,
  controlsPaused = false,
}: CallDockProps): ReactNode {
  return (
    <section
      className="cc-call-dock"
      id="cc-call-dock"
      aria-label="Active call · Available across the customer file"
    >
      <div className="cc-dock-row">
        <div className="cc-dock-caller">
          <div className="cc-call-status">
            <div className="cc-call-avatar" id="cc-call-avatar">
              {info.initials}
            </div>
            <div>
              <span className="cc-channel-label" id="cc-call-direction">
                {info.directionLabel}
              </span>
              <h3 id="cc-call-name">{info.callerName}</h3>
              <p id="cc-call-number">{info.callerNumber}</p>
            </div>
          </div>
          <div className="cc-dock-channel" id="cc-dock-channel">
            <span className="badge" id="cc-identity-badge">
              {info.providerLabel}
            </span>
          </div>
        </div>
        <div className="cc-dock-controls">
          <div className="cc-call-clock">
            <span id="cc-call-state" className="cc-state-pill">
              {info.state}
            </span>
            {info.duration ? <strong id="cc-call-duration">{info.duration}</strong> : null}
            <small id="cc-provider-evidence">{info.providerEvidence}</small>
          </div>
          <div className="cc-audio-actions" id="cc-audio-actions">
            <WriteButton
              paused={controlsPaused}
              className="cc-end-call"
              id="cc-end-call"
              onClick={onEndCall}
              actionLabel="End call"
            >
              <Icon id="help" />
              End call
            </WriteButton>
            <WriteButton
              paused={controlsPaused}
              className="cc-transfer-button"
              id="cc-transfer"
              onClick={onTransfer}
              actionLabel="Transfer call"
            >
              <Icon id="people" />
              Transfer call
            </WriteButton>
            <WriteButton
              paused={controlsPaused}
              className="cc-secondary"
              id="cc-mute"
              onClick={onMute}
              actionLabel="Mute"
            >
              <Icon id="help" />
              <span id="cc-mute-label">{muted ? 'Unmute' : 'Mute'}</span>
            </WriteButton>
          </div>
        </div>
      </div>
      <div className="cc-dock-return">
        <button type="button" id="cc-return-to-call">
          Call guide &amp; notes
        </button>
        <small id="cc-call-retained">Notes stay with this call</small>
      </div>
    </section>
  );
}
